import { styled } from '@mui/material/styles';
import { Box, TableCell, TableRow, Button, IconButton, InputBase, Chip, Drawer, Toolbar, List, ListItemIcon, Divider, Typography } from '@mui/material';
import { colors } from '@/theme';

export const PortalContainer = styled(Box)(({ theme }) => ({
  background: colors.neutralGrey300,
  height: '100%',
  padding: 0,
}));

export const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '40px 0 40px 40px',
  background: '#fff',
}));

export const FedExLogo = styled('img')({
  height: 32,
  marginRight: 12,
});

export const Title = styled('span')({
  fontFamily: 'PF DIN Text Pro',
  fontWeight: 700,
  fontSize: 28,
  color: '#222B45',
  letterSpacing: 0.2,
});

export const Card = styled(Box)(({ theme }) => ({
  background: '#fff',
  borderRadius: 16,
  margin: '24px',
  width: 'auto',
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  padding: '0px 16px',
  border: 'none',
}));

export const SearchFilterRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '24px 32px 0 32px',
  justifyContent: 'space-between',
  gap: 16,
}));

export const SearchInputWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  background: '#fff',
  border: '1px solid #E7E7E7',
  borderRadius: 24,
  padding: '2px 16px',
  width: 420,
  height: 40,
  '& input': {
    padding: 0,
    height: '100%',
    boxSizing: 'border-box',
  },
}));

export const SearchInput = styled(InputBase)(({ theme }) => ({
  fontSize: 16,
  fontFamily: 'PF DIN Text Pro',
  width: '100%',
  color: '#222B45',
}));

export const FilterButton = styled(Button)(({ theme }) => ({
  background: '#fff',
  borderRadius: 24,
  color: '#4E5D75',
  fontWeight: 500,
  fontFamily: 'PF DIN Text Pro',
  textTransform: 'none',
  padding: '10px 20px 10px 16px',
  boxShadow: 'none',
  minWidth: 180,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  '&:hover': {
    background: '#F0F1F2',
    boxShadow: 'none',
  },
}));


export const FilterWrapper = styled(Box)(({ theme }) => ({
  position:"relative",
}));

export const FilterBox = styled(Box)(({ theme }) => ({
  position:"absolute",
  top:"100%",
  right:0,
  display:"flex",
  justifyContent:"center",
  gap:5,
  background:colors.white,
  border:"1px solid "+colors.brainstemGrey,
  borderRadius:10,
}));

export const FilterIcon = styled(Button)(({ theme }) => ({
  display:"flex",
  flexDirection:"column",
  alignItems:"center",
  gap:2
}));

export const Text = styled(Typography)(({ theme }) => ({
  color: '#4E5D75',
  fontWeight: 500,
  fontFamily: 'PF DIN Text Pro',
  textTransform: 'none',
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:not(:last-child)': {
    borderBottom: '1px solid #E7E7E7',
  },
  background: '#fff',
}));

export const StatusChip = styled(Chip)(({ theme }) => ({
  fontWeight: 700,
  fontFamily: 'PF DIN Text Pro',
  fontSize: 16,
  borderRadius: 8,
  padding: '0 8px',
  height: 32,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  background: 'transparent',
  boxShadow: 'none',
}));

export const PrintButton = styled(Button)(({ theme }) => ({
  background: '#0076FF',
  color: '#fff',
  fontWeight: 500,
  fontFamily: 'PF DIN Text Pro',
  borderRadius: 8,
  textTransform: 'none',
  padding: '8px 20px',
  fontSize: 18,
  boxShadow: 'none',
  '&:hover': {
    background: '#0059c1',
    boxShadow: 'none',
  },
}));

export const PrintIconButton = styled(IconButton)(({ theme }) => ({
  color: '#4E5D75',
  marginLeft: 8,
}));

export const TableCellBold = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'header',
})<{ header?: boolean }>(({ theme, header }) => ({
  fontWeight: 700,
  fontFamily: 'PF DIN Text Pro',
  fontSize: 16,
  color: '#222B45',
  background: '#fff',
  borderBottom: `1px solid ${colors.neutralGrey800}`,
  borderTop: header ? `1px solid ${colors.neutralGrey800}` : undefined,
  padding: '16px',
}));

export const TableCellNormal = styled(TableCell)(({ theme }) => ({
  fontWeight: 400,
  fontFamily: 'PF DIN Text Pro',
  fontSize: 16,
  color: '#222B45',
  background: '#fff',
  borderBottom: `1px solid ${colors.neutralGrey800}`,
  padding: '16px',
}));

export const ExpandedDetailsBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  padding: '24px 32px 24px 0',
  background: '#F7F8FA',
  minHeight: 120,
  gap: 12,
}));

export const SidebarDrawer = styled(Drawer)(({ theme }) => ({
  width: 240,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: 240,
    boxSizing: 'border-box',
  },
}));

export const SidebarToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: 32,
}));

export const LogoBox = styled(Box)(({ theme }) => ({
  paddingInlineStart: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(6),
}));

export const LogoImage = styled('img')({
  width: 135,
  height: 54,
  marginBottom: 4,
});

export const LogoText = styled(Box)(({ theme }) => ({
  color: '#0076FF',
  fontWeight: 700,
  fontSize: 16,
  fontFamily: 'PF DIN Text Pro',
  letterSpacing: 0.5,
  marginBottom: theme.spacing(1),
}));

export const SidebarList = styled(List)(({ theme }) => ({
  paddingInlineStart: theme.spacing(2),
  paddingInlineEnd: theme.spacing(3),
}));

export const SidebarListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: 32,
  marginRight: theme.spacing(1),
}));

export const SidebarDivider = styled(Divider)(({ theme }) => ({
  borderColor: theme.palette.grey[800],
})); 